'use client';

import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import type { Locale } from '@boty/i18n';

const LOCALE_PREFERENCE_KEY = 'boty_locale_preference';

export function saveLocalePreference(locale: Locale): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(LOCALE_PREFERENCE_KEY, locale);
}

/**
 * Persists the URL's current locale to localStorage so the LanguageSwitcher
 * stays in sync with the user's most recent intent.
 *
 * Why no client-side redirect:
 * - next-intl middleware already routes "/" to the right locale based on
 *   browser headers on the server. By the time the page loads, the URL
 *   locale represents either the user's explicit click (e.g. /he/boty-help-he)
 *   or the middleware's browser-locale detection.
 * - Redirecting based on browser locale here would clobber explicit links
 *   (clicking /he/... from English browser would flip back to /en/...).
 */
export function useAutoLocale(): void {
  const currentLocale = useLocale() as Locale;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LOCALE_PREFERENCE_KEY, currentLocale);
  }, [currentLocale]);
}
